import convert from "convert-units";
import { round } from "lodash";
import moment from "moment";
import { useState } from "react";
import submit from "submit-form";
import { getEditableValue } from "../../utils/converters/convert-units";
import { goalValidation } from "../../utils/goals-validations";

// Shared by AddGoal.js's own personalRecord branch and Periodization's
// manage-milestone-goals-modal (PersonalRecordGoalsSection.js) - state,
// prefill, validation and submit-payload assembly for a Personal Record
// goal, so a fix here (the targetExercise-wiped-on-edit bug, the targetDate
// UTC-parsing gotcha, the missing date-must-be-future check) only ever
// needs making once. `goal` is the existing PR being edited, or
// null/undefined to create a new one. Each caller supplies its own
// `buildUrl(id)` since they hit different actions with different
// close-vs-stay-open behavior on save (AddGoal.js closes the modal,
// manage-milestone-goals-modal keeps it open).
export const usePersonalRecordGoalForm = ({
  goal,
  units,
  movementOptions,
  assert,
  flash,
  buildUrl,
}) => {
  const initialSelectedExercise = goal ? goal.details.targetName : "";
  const initialTargetReps = goal ? goal.details.targetReps : "";
  const initialTargetLoad = goal
    ? getEditableValue(goal.details.targetLoad, units)
    : "";
  const initialTargetDate = goal
    ? // Explicit format, not moment's ISO auto-detect - see
      // useBodyweightGoalForm's own comment on this exact off-by-one.
      moment(goal.targetDate, "YYYY-MM-DD").toDate()
    : moment().add(1, "month").toDate();

  const [selectedExercise, setSelectedExercise] = useState(
    initialSelectedExercise
  );
  // Editing without re-picking from the dropdown used to wipe
  // targetPrimary/targetSecondary/targetType (they're only ever set via
  // onSelectExercise below, so this stayed null on edit and
  // submitPersonalRecord read `targetExercise || {}` - all three silently
  // went undefined on save). Matched by label (what selectedExercise/
  // goal.details.targetName already is) - falls back to null exactly like
  // before if the exercise was renamed/removed from the library since.
  const [targetExercise, setTargetExercise] = useState(() =>
    goal
      ? movementOptions.find(
          (option) => option.label === goal.details.targetName
        ) || null
      : null
  );
  const [targetReps, setTargetReps] = useState(initialTargetReps);
  const [targetLoad, setTargetLoad] = useState(initialTargetLoad);
  const [targetUnits, setTargetUnits] = useState(units);
  const [targetDate, setTargetDate] = useState(initialTargetDate);

  const onSelectExercise = (val) => {
    setSelectedExercise(val.label);
    setTargetExercise(val);
  };

  const isBlank = !selectedExercise || !targetReps || !targetLoad;
  // Stable across re-renders since goal/units don't change while this form
  // is open (nothing here triggers a page refresh until Save is clicked) -
  // callers combine this with `isBlank` for their own Save button, since not
  // every caller disables Save on no-op (AddGoal.js never has;
  // manage-milestone-goals-modal does).
  const isUnchanged =
    !!goal &&
    targetUnits === units &&
    selectedExercise === initialSelectedExercise &&
    String(targetReps) === String(initialTargetReps) &&
    String(targetLoad) === String(initialTargetLoad) &&
    moment(targetDate).isSame(initialTargetDate, "day");

  const submitPersonalRecord = () => {
    const convertedLoad =
      targetUnits === "metric"
        ? targetLoad
        : round(convert(targetLoad).from("lb").to("kg"), 5);
    const { primary, secondary, type } = targetExercise || {};
    const details = {
      targetName: selectedExercise,
      targetPrimary: primary,
      targetSecondary: secondary,
      targetType: type,
      targetReps: Number(targetReps),
      targetLoad: Number(convertedLoad),
    };

    const utcTargetDate = Number(moment(targetDate).format("YYYYMMDD"));
    if (
      !goalValidation("personalRecord", details, assert, flash, utcTargetDate)
    ) {
      return;
    }

    const data = {
      startDate: moment().format("YYYY-MM-DD"),
      utcStartDate: Number(moment().format("YYYYMMDD")),
      type: "personalRecord",
      details,
      utcTargetDate,
      targetDate: moment(targetDate).format("YYYY-MM-DD"),
    };

    submit(buildUrl(goal?._id), { method: "POST", body: data });
  };

  return {
    selectedExercise,
    onSelectExercise,
    targetReps,
    setTargetReps,
    targetLoad,
    setTargetLoad,
    targetUnits,
    setTargetUnits,
    targetDate,
    setTargetDate,
    isBlank,
    isUnchanged,
    submitPersonalRecord,
  };
};
